import React, { useState, useEffect } from 'react';
import { User, TrendingUp, Calendar, Award, Target, Users } from 'lucide-react';

const OfficerAnalysisTab = ({ isDark = false, selectedPeriod = 'month' }) => {
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOfficerData();
  }, [selectedPeriod]);

  const fetchOfficerData = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual API call
      // const response = await fetch(`/api/officer-analysis?period=${selectedPeriod}`);
      // const data = await response.json();
      
      // Mock data for demonstration
      const mockData = [
        {
          id: 1,
          name: "John Kamau",
          meetingsAttended: 15,
          groupsManaged: 8,
          totalSavingsCollected: 1250000,
          totalLoansRepaid: 2100000,
          averageAttendanceRate: 92,
          performanceRating: "Excellent"
        },
        {
          id: 2,
          name: "Sarah Njeri",
          meetingsAttended: 12,
          groupsManaged: 6,
          totalSavingsCollected: 980000,
          totalLoansRepaid: 1600000,
          averageAttendanceRate: 88,
          performanceRating: "Very Good"
        }
      ];

      setOfficers(mockData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching officer data:', error);
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Loading officer analysis...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Officer Analysis
        </h2>
        <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
          Performance metrics and analytics for field officers
        </p>
      </div>

      {/* Officer Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {officers.map(officer => (
          <div
            key={officer.id}
            className={`p-6 rounded-lg border ${
              isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            {/* Officer Header */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  isDark ? 'bg-blue-900/30' : 'bg-blue-100'
                }`}>
                  <User className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {officer.name}
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Field Officer
                  </p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                officer.performanceRating === 'Excellent'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {officer.performanceRating}
              </span>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-[#1f262e]' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Meetings
                  </span>
                </div>
                <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {officer.meetingsAttended}
                </div>
              </div>

              <div className={`p-4 rounded-lg ${isDark ? 'bg-[#1f262e]' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Users className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                  <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Groups
                  </span>
                </div>
                <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {officer.groupsManaged}
                </div>
              </div>

              <div className={`p-4 rounded-lg ${isDark ? 'bg-[#1f262e]' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Savings
                  </span>
                </div>
                <div className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {formatCurrency(officer.totalSavingsCollected)}
                </div>
              </div>

              <div className={`p-4 rounded-lg ${isDark ? 'bg-[#1f262e]' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className={`w-4 h-4 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                  <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Repayments
                  </span>
                </div>
                <div className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {formatCurrency(officer.totalLoansRepaid)}
                </div>
              </div>
            </div>

            {/* Attendance Rate */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Average Attendance Rate
                </span>
                <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {officer.averageAttendanceRate}%
                </span>
              </div>
              <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500"
                  style={{ width: `${officer.averageAttendanceRate}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary Section */}
      <div className={`p-6 rounded-lg ${isDark ? 'bg-[#2b323c]' : 'bg-white'}`}>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Period Summary
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`p-4 rounded-lg border ${isDark ? 'bg-[#1f262e] border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            <Target className={`w-8 h-8 mb-3 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Total Meetings
            </div>
            <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {officers.reduce((sum, o) => sum + o.meetingsAttended, 0)}
            </div>
          </div>

          <div className={`p-4 rounded-lg border ${isDark ? 'bg-[#1f262e] border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            <Award className={`w-8 h-8 mb-3 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
            <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Total Collections
            </div>
            <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {formatCurrency(officers.reduce((sum, o) => sum + o.totalSavingsCollected, 0))}
            </div>
          </div>

          <div className={`p-4 rounded-lg border ${isDark ? 'bg-[#1f262e] border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            <TrendingUp className={`w-8 h-8 mb-3 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
            <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Total Repayments
            </div>
            <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {formatCurrency(officers.reduce((sum, o) => sum + o.totalLoansRepaid, 0))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfficerAnalysisTab;